import type {CompRef, Pointer, ChildrenVariablesNode, ParentsVariablesNode} from '@wix/document-services-types'
import {pointerUtils, type DAL, type ExtensionAPI} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import * as constants from '../../constants/constants'
import type {VariablesExtensionAPI} from './variables'

const {getPointer} = pointerUtils

const VARIABLES_NAMESPACE = constants.DATA_TYPES.variables

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

export const collectVariableConnections = (
    dal: DAL,
    variableConnections: object
): {propertyPath: string; variablePointer: Pointer}[] => {
    const variablePointersToResolve: {propertyPath: string; variablePointer: Pointer}[] = []

    for (const [propertyPath, variableId] of Object.entries(variableConnections)) {
        if (typeof variableId === 'string') {
            const referencedVariableId = variableId.replace('#', '')
            const referencedVariablePointer = getPointer(referencedVariableId, VARIABLES_NAMESPACE)

            if (!dal.has(referencedVariablePointer)) {
                throw createVariablesError('Referenced variable does not exist')
            }

            variablePointersToResolve.push({
                propertyPath,
                variablePointer: referencedVariablePointer
            })
        }
    }

    return variablePointersToResolve
}

export const createVariableDependencyTree = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    variablePointers: Pointer[],
    compPointer: CompRef
): Record<string, {variable: Pointer; dependencies: string[]; dependents: string[]}> => {
    const {variables} = extensionAPI as VariablesExtensionAPI
    const dependencyTree: Record<string, {variable: Pointer; dependencies: string[]; dependents: string[]}> = {}

    // Initialize the tree with all variables
    for (const variablePointer of variablePointers) {
        dependencyTree[variablePointer.id] = {
            variable: variablePointer,
            dependencies: [],
            dependents: []
        }
    }

    // Build dependency relationships
    for (const variablePointer of variablePointers) {
        const variableData = variables.getVariableData(compPointer, variablePointer, false)
        if (!variableData) continue

        // Check if this variable has variableConnections
        if (variableData.variableConnections && typeof variableData.variableConnections === 'object') {
            const variableConnections = collectVariableConnections(dal, variableData.variableConnections)

            for (const {variablePointer: dependentVariablePointer} of variableConnections) {
                const dependentId = dependentVariablePointer.id

                // Add dependency relationship
                if (dependencyTree[variablePointer.id] && dependencyTree[dependentId]) {
                    // variablePointer has connections to dependentVariablePointer
                    // So variablePointer is the parent, dependentVariablePointer is the child
                    dependencyTree[variablePointer.id].dependents.push(dependentId)
                    dependencyTree[dependentId].dependencies.push(variablePointer.id)
                }
            }
        }
    }

    return dependencyTree
}

export const getChildrenTree = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    parentVariablePointer: Pointer,
    variablePointers: Pointer[],
    compPointer: CompRef
): ChildrenVariablesNode => {
    const dependencyTree = createVariableDependencyTree(dal, extensionAPI, variablePointers, compPointer)
    const visited = new Set<string>()
    const currentPath = new Set<string>()

    const buildChildrenTree = (variableId: string): {pointer: Pointer; children: Record<string, any>} => {
        if (currentPath.has(variableId)) {
            // Handle circular dependencies by returning empty children
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), children: {}}
        }

        if (visited.has(variableId)) {
            // Node already processed, return empty children to avoid duplication
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), children: {}}
        }

        visited.add(variableId)
        currentPath.add(variableId)

        const node = dependencyTree[variableId]

        if (!node) {
            currentPath.delete(variableId)
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), children: {}}
        }

        const children: Record<string, any> = {}

        for (const dependentId of node.dependents) {
            // Skip if this would create a circular dependency
            if (!currentPath.has(dependentId)) {
                const childTree = buildChildrenTree(dependentId)
                children[dependentId] = {
                    pointer: childTree.pointer,
                    children: childTree.children
                }
            }
        }

        currentPath.delete(variableId)

        return {
            pointer: node.variable,
            children
        }
    }

    return buildChildrenTree(parentVariablePointer.id)
}

export const getParentsTree = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    childVariablePointer: Pointer,
    variablePointers: Pointer[],
    compPointer: CompRef
): ParentsVariablesNode => {
    const dependencyTree = createVariableDependencyTree(dal, extensionAPI, variablePointers, compPointer)
    const visited = new Set<string>()
    const currentPath = new Set<string>()

    const buildParentsTree = (variableId: string) => {
        if (currentPath.has(variableId)) {
            // Handle circular dependencies by returning empty parents
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), parents: {}}
        }

        if (visited.has(variableId)) {
            // Node already processed, return empty parents to avoid duplication
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), parents: {}}
        }

        visited.add(variableId)
        currentPath.add(variableId)

        const node = dependencyTree[variableId]

        if (!node) {
            currentPath.delete(variableId)
            return {pointer: getPointer(variableId, VARIABLES_NAMESPACE), parents: {}}
        }

        const parents: Record<string, any> = {}

        // Add all dependencies as parents (since if A has connections to B, A is parent, B is child)
        // In dependency tree, if A depends on B, then B is the parent of A
        for (const dependencyId of node.dependencies) {
            // Skip if this would create a circular dependency
            if (!currentPath.has(dependencyId)) {
                const parentTree = buildParentsTree(dependencyId)
                parents[dependencyId] = {
                    pointer: parentTree.pointer,
                    parents: parentTree.parents
                }
            }
        }

        currentPath.delete(variableId)

        return {
            pointer: node.variable,
            parents
        }
    }

    return buildParentsTree(childVariablePointer.id)
}
